import { useState, useEffect, useRef } from 'react';
import { get } from '../utils/fetch';
import camelCaseKeys from 'camelcase-keys';
import { Log } from '../../../clients/sidebar/models/Task';

type Props = {
  taskId: number | null;
  projectId: number;
  bugherdUrl: string;
  enabled?: boolean;
};

export const useLogs = ({
  taskId,
  projectId,
  bugherdUrl,
  enabled = true
}: Props): {
  logs: Log[];
  addLog: (taskLog: Log) => void;
  loading: boolean;
} => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const taskIdRef = useRef(taskId);
  const enabledRef = useRef(enabled);

  taskIdRef.current = taskId;
  enabledRef.current = enabled;

  const endpoint = `${bugherdUrl}/projects/${projectId}/tasks/${taskId}/comments/logs`;

  useEffect(() => {
    let cancelled = false;

    setLogs([]);

    if (!enabled || !taskId || !projectId || !bugherdUrl) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const getLogs = async () => {
      setLoading(true);
      try {
        const response = await get(endpoint);
        if (!cancelled) setLogs(camelCaseKeys(response, { deep: true }));
      } catch (error) {
        if (!cancelled) console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getLogs();

    return () => {
      cancelled = true;
    };
  }, [taskId, projectId, bugherdUrl, enabled, endpoint]);

  const addLog = (log: Log) => {
    if (!log) return;

    const logTaskId = log.taskId || (log as any).task_id;

    if (!enabledRef.current || logTaskId !== taskIdRef.current) return;

    setLogs(prevLogs =>
      prevLogs.some(existingLog => existingLog.id === log.id)
        ? prevLogs
        : [log, ...prevLogs]
    );
  };

  return { logs, addLog, loading };
};
